import { Icon, Typography } from "@/components/atoms";
import { clsx } from "@/utils";

const driverAppTasks = [
  "Manage job bookings",
  "Manage clients and invitations",
  "Manage your private networks",
  "Manage your vehicle",
  "Update your trip pricing",
  "Close and complete job bookings",
  "View your payouts",
];

const webPortalTasks = ["Manage your subscription", "Set up and manage payments", "View billing and invoices"];

/** Every text run in this panel is on the 20px line grid, which none of the Typography defaults use. */
const LEADING = "!leading-5";

const TaskList = ({ tasks }: { tasks: string[] }) => (
  <ul className="list-disc pl-5">
    {tasks.map((task) => (
      <li key={task}>
        <Typography variant="paragraph" className={clsx(LEADING, "text-neutral-dark-gray")}>
          {task}
        </Typography>
      </li>
    ))}
  </ul>
);

/**
 * `bg-primary-light` is branded: `useBranding` overrides it per operator at runtime. Its static
 * default matches the design, so it is left as a token rather than pinned to the design's hex.
 */
const IconTile = ({ name }: { name: string }) => (
  <div className="flex h-8 w-8 items-center justify-center rounded bg-primary-light">
    <Icon name={name} size="lg" className="text-primary" />
  </div>
);

const Paragraph = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="paragraph" className={clsx(LEADING, "text-neutral-dark-gray")}>
    {children}
  </Typography>
);

/** The 20px gaps inside each body reproduce the blank lines the design uses between paragraphs. */
export const HowYouWillUseRideMinder = () => (
  <div className="flex flex-col gap-4">
    <Typography variant="title" className="!leading-5 text-neutral-black">
      How you&rsquo;ll use RideMinder as a Solo Operator
    </Typography>

    <div className="overflow-hidden rounded-lg border border-neutral-mid-gray">
      <div className="flex flex-col gap-2.5 border-b border-neutral-mid-gray bg-neutral-surface-gray px-5 py-4">
        <div className="flex flex-col gap-4">
          <IconTile name="Mobile" />

          <div className="flex flex-col gap-2">
            <Typography variant="action" className={clsx(LEADING, "text-neutral-dark-gray")}>
              Driver App (Mobile)
            </Typography>

            <div className="flex flex-col gap-5">
              <Paragraph>
                This is where you&rsquo;ll do <span className="font-bold">all day-to-day work.</span>
              </Paragraph>

              <div>
                <Paragraph>Use the Driver App on your phone to:</Paragraph>
                <TaskList tasks={driverAppTasks} />
              </div>

              <Paragraph>Accessed on your mobile device</Paragraph>
            </div>
          </div>
        </div>
      </div>

      {/* The design fixes this row at 384px tall. Applied as a minimum so longer copy or a
          translation grows the row instead of being clipped. */}
      <div className="flex min-h-96 flex-col gap-4 bg-neutral-surface-gray px-5 py-4">
        <div className="flex flex-col gap-4">
          <IconTile name="Global" />

          <div className="flex flex-col gap-2">
            <Typography variant="action" className={clsx(LEADING, "text-neutral-dark-gray")}>
              Web Portal (You&rsquo;re here now)
            </Typography>

            <div className="flex flex-col gap-5">
              <Paragraph>
                You&rsquo;re currently logged into your <span className="font-bold">Solo Operator web portal.</span>
              </Paragraph>

              <div>
                <Paragraph>Use this portal to:</Paragraph>
                <TaskList tasks={webPortalTasks} />
              </div>

              <Paragraph>
                All bookings and job management happen in the <span className="font-bold">Driver App on your mobile.</span>
              </Paragraph>
            </div>

            <div className="flex items-center justify-center rounded-lg border border-neutral-mid-gray bg-neutral-gray p-2.5">
              <Typography variant="small-regular" className="!font-medium !leading-4 text-neutral-dark-gray">
                Jobs you offload include a 20% operator commission, paid to you and visible in payout history
              </Typography>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);
